/* ── Shared inline styles ─────────────────────────────────────── */
const card: React.CSSProperties = {
  background: '#0c1520',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '0.625rem',
  padding: '0.875rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  minWidth: 0,
};

const cardHeader: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: '0.5625rem',
  fontWeight: 700,
  color: '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const sectionLabel: React.CSSProperties = {
  fontSize: '0.5rem',
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginTop: '0.25rem',
};

const metricRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.6875rem',
  color: '#d1d5db',
};

const metricVal: React.CSSProperties = {
  fontWeight: 700,
  fontFamily: 'ui-monospace, monospace',
  color: '#e5e7eb',
};

const caption: React.CSSProperties = {
  fontSize: '0.5625rem',
  color: '#4b5563',
  fontStyle: 'italic',
  marginTop: 'auto',
  lineHeight: 1.4,
};

const dot = (color: string): React.CSSProperties => ({
  width: 6, height: 6, borderRadius: '50%', background: color, opacity: 0.7,
});

const barSegment = (color: string, pct: number): React.CSSProperties => ({
  height: 8, borderRadius: 4, background: color, flex: `0 0 ${pct}%`,
});

/* ── Mini drawdown sparkline using pure CSS ───────────────────── */
function DrawdownSparkline() {
  const points = [0, -5, -12, -8, -20, -38, -30, -22, -15, -8, -3, 0, -6, -18, -12, -5, 0];
  const max = 0;
  const min = -40;
  const w = 100;
  const h = 40;
  const d = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = ((p - max) / (min - max)) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 36 }} preserveAspectRatio="none">
      <path d={d} fill="none" stroke="#ef4444" strokeWidth="1.5" opacity="0.7" />
    </svg>
  );
}

/* ── Market presence mini chart ───────────────────────────────── */
function MarketPresenceChart() {
  const bars = [18, 30, 45, 60, 38, 55, 70, 40, 25, 50, 65, 35, 20, 42, 58, 72, 48, 30, 55, 40];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 32 }}>
      {bars.map((h, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: `${h}%`,
            background: i < bars.length * 0.3 ? '#06b6d4' : i < bars.length * 0.7 ? '#22d3ee' : '#67e8f9',
            borderRadius: 1,
            opacity: 0.7,
          }}
        />
      ))}
    </div>
  );
}

export default function BacktestResultsMock() {
  return (
    <div className="platform-mock" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif' }}>
      {/* Top toolbar */}
      <div className="platform-mock-header" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ color: '#f97316', fontSize: '0.625rem' }}>BACKTEST</span>
          <span style={{ color: '#9ca3af', fontSize: '0.625rem' }}>BTCUSDT &middot; 1d &middot; Completed 3/17/2026</span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.5625rem', color: '#9ca3af' }}>
          {['Results', 'Trades', 'Chart', 'Monte Carlo', 'Editor'].map((tab, i) => (
            <span key={tab} style={i === 0 ? { color: '#e5e7eb', fontWeight: 700 } : undefined}>{tab}</span>
          ))}
        </div>
      </div>

      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.625rem' }}>

          {/* ─ METRICS ─ */}
          <div style={card}>
            <div style={cardHeader}><span style={dot('#3b82f6')} /> Metrics</div>
            <div style={sectionLabel}>Structure &amp; Edge</div>
            <div style={metricRow}><span>Payoff</span><span style={metricVal}>2.6x</span></div>
            <div style={metricRow}><span>Profit Factor</span><span style={metricVal}>2.13</span></div>
            <div style={sectionLabel}>Trade Characteristics</div>
            <div style={metricRow}><span>Trades</span><span style={metricVal}>31</span></div>
            <div style={metricRow}><span>Win Rate</span><span style={metricVal}>45%</span></div>
            <div style={sectionLabel}>Risk-Adjusted</div>
            <div style={metricRow}><span>Sharpe (ann.)</span><span style={metricVal}>0.83</span></div>
            <div style={metricRow}><span>Expectancy</span><span style={metricVal}>$9.3k</span></div>
            <div style={{ ...caption, marginTop: '0.5rem' }}>Historical return: <span style={{ color: '#22c55e', fontWeight: 700 }}>+2870.0%</span></div>
          </div>

          {/* ─ BEHAVIOUR ─ */}
          <div style={card}>
            <div style={cardHeader}><span style={dot('#f59e0b')} /> Behaviour</div>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#e5e7eb' }}>Strong Edge Profile</div>
            <div style={sectionLabel}>Primary Drivers</div>
            <div style={{ fontSize: '0.625rem', color: '#9ca3af', lineHeight: 1.5 }}>
              Large winners dominate outcomes<br />
              Positive expectancy observed<br />
              Losses are capped relative to wins
            </div>
            <div style={caption}>Consistency: <span style={{ color: '#e5e7eb', fontWeight: 600 }}>Stable within tested period</span></div>
          </div>

          {/* ─ TRADE STRUCTURE ─ */}
          <div style={card}>
            <div style={cardHeader}><span style={dot('#22c55e')} /> Trade Structure</div>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#e5e7eb' }}>Concentrated Winner Profile</div>
            <div style={{ display: 'flex', gap: 4, marginTop: '0.25rem' }}>
              <div style={{ flex: 14, height: 8, borderRadius: 4, background: '#22c55e' }} />
              <div style={{ flex: 17, height: 8, borderRadius: 4, background: '#ef4444' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.5rem', color: '#6b7280' }}>
              <span>Wins (14)</span><span>Losses (17)</span>
            </div>
            <div style={sectionLabel}>Characteristics</div>
            <div style={{ fontSize: '0.625rem', color: '#9ca3af', lineHeight: 1.5 }}>
              Top 20% trades: 91% of gains<br />
              Payoff ratio: 2.6x<br />
              Median duration: 33d
            </div>
            <div style={caption}>Profit driven by a subset of large winning trades.</div>
          </div>

          {/* ─ EXPOSURE ─ */}
          <div style={card}>
            <div style={cardHeader}><span style={dot('#06b6d4')} /> Exposure</div>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#e5e7eb' }}>Position-Holding Exposure</div>
            <MarketPresenceChart />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.5rem', color: '#6b7280' }}>
              <span>Start</span><span>End</span>
            </div>
            <div style={sectionLabel}>Characteristics</div>
            <div style={{ fontSize: '0.625rem', color: '#9ca3af', lineHeight: 1.5 }}>
              Long-only (31 trades)<br />
              Avg duration: 52d<br />
              Max gap: 214d
            </div>
            <div style={caption}>Holds long positions through extended periods.</div>
          </div>

          {/* ─ REGIMES ─ */}
          <div style={card}>
            <div style={cardHeader}><span style={dot('#10b981')} /> Regimes</div>
            <div style={sectionLabel}>Volatility Distribution</div>
            <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden' }}>
              <div style={barSegment('#3b82f6', 45)} />
              <div style={barSegment('#ef4444', 37)} />
              <div style={barSegment('#f59e0b', 18)} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.5rem', color: '#9ca3af' }}>
              <span><span style={{ ...dot('#3b82f6'), display: 'inline-block', width: 5, height: 5, marginRight: 3 }} />Low 45%</span>
              <span><span style={{ ...dot('#ef4444'), display: 'inline-block', width: 5, height: 5, marginRight: 3 }} />High 37%</span>
              <span><span style={{ ...dot('#f59e0b'), display: 'inline-block', width: 5, height: 5, marginRight: 3 }} />Medium 19%</span>
            </div>
            <div style={sectionLabel}>Trend Structure</div>
            <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden' }}>
              <div style={barSegment('#22c55e', 55)} />
              <div style={barSegment('#f59e0b', 24)} />
              <div style={barSegment('#8b5cf6', 21)} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.5rem', color: '#9ca3af' }}>
              <span>Trending 55%</span>
              <span>Ranging 24%</span>
              <span>Transition 21%</span>
            </div>
            <div style={caption}>No single regime dominates the tested period.</div>
          </div>

          {/* ─ DRAWDOWN ─ */}
          <div style={card}>
            <div style={cardHeader}><span style={dot('#ef4444')} /> Drawdown</div>
            <DrawdownSparkline />
            <div style={metricRow}><span style={sectionLabel}>Depth</span><span style={{ ...metricVal, color: '#ef4444' }}>38.7%</span></div>
            <div style={sectionLabel}>Duration</div>
            <div style={metricRow}><span>Longest</span><span style={metricVal}>986d</span></div>
            <div style={metricRow}><span>Median recovery</span><span style={metricVal}>233d</span></div>
            <div style={metricRow}><span>Recovery factor</span><span style={metricVal}>74.1x</span></div>
            <div style={caption}>Deep drawdowns observed; recovery supported by asymmetric payoff.</div>
          </div>

          {/* ─ DRAWDOWN ENVELOPE ─ */}
          <div style={card}>
            <div style={cardHeader}><span style={dot('#f97316')} /> Drawdown Envelope</div>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#e5e7eb' }}>Observed vs Simulated</div>
            <div style={{ fontSize: '0.5625rem', color: '#9ca3af' }}>Actual drawdown: 21.3%</div>
            <div style={sectionLabel}>Observed Drawdown Position</div>
            <div style={{ background: '#1e293b', borderRadius: 4, height: 14, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', left: '48%', top: 0, bottom: 0, width: 2, background: '#e5e7eb' }} />
              <div style={{ position: 'absolute', top: 2, left: '44%', fontSize: '0.4375rem', color: '#d1d5db' }}>48th percentile</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.4375rem', color: '#6b7280' }}>
              <span>P5: 8.1%</span><span>P95: 72.4%</span>
            </div>
            <div style={{ fontSize: '0.625rem', color: '#9ca3af', lineHeight: 1.5 }}>
              1,000 randomized trade orderings
            </div>
            <div style={caption}>Observed drawdown is consistent with expected sequencing variation.</div>
          </div>

          {/* ─ PARAMETERS ─ */}
          <div style={card}>
            <div style={cardHeader}><span style={dot('#8b5cf6')} /> Parameters</div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)',
              borderRadius: 4, padding: '0.125rem 0.375rem', fontSize: '0.5625rem',
              color: '#f59e0b', fontWeight: 700, alignSelf: 'flex-start',
            }}>
              Exploratory
            </div>
            <div style={{ fontSize: '0.5625rem', color: '#06b6d4' }}>Not yet validated</div>
            <div style={sectionLabel}>Current Configuration</div>
            <div style={metricRow}><span>Fast Length</span><span style={metricVal}>20</span></div>
            <div style={metricRow}><span>Slow Length</span><span style={metricVal}>40</span></div>
            <div style={caption}>Performance at this stage does not imply robustness.</div>
          </div>
        </div>
      </div>
      <div className="platform-mock-caption">
        The results dashboard: eight analysis cards covering metrics, behaviour, trade structure, exposure, regimes, drawdown, Monte Carlo envelope, and parameters.
      </div>
    </div>
  );
}
